<template>
  <div class="av-modal">
    <button
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="isModalOpen"
      aria-controls="modal-content"
      aria-describedby="date-format-hint"
      :class="[
        'av-input-button',
        'outline-none',
        { 'input-selected': moveDate !== null, disabled: doesNotHaveMoveDate }
      ]"
      @click="openModal"
    >
      <span class="selected-date" :class="{ 'disabled-text': doesNotHaveMoveDate }">{{
        displayText
      }}</span>
      <span class="icon-heavy-calendar-1" aria-hidden="true" />
    </button>

    <teleport to="body">
      <Transition name="modal" appear>
        <div
          v-if="isModalOpen"
          class="modal-overlay"
          aria-modal="true"
          role="dialog"
          @click="handleOverlayClick"
        >
          <div id="modal-content" class="modal-content" @click.stop>
            <div class="modal-header">
              <h5 class="modal-title">{{ props.text.chooseDate }}</h5>
              <button class="modal-close" aria-label="Close" @click.prevent="closeModal">
                <svg
                  class="modal-close-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <g clip-path="url(#clip0_2539_8277)">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L8 6.58579L14.2929 0.292893C14.6834 -0.0976311 15.3166 -0.0976311 15.7071 0.292893C16.0976 0.683417 16.0976 1.31658 15.7071 1.70711L9.41421 8L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L8 9.41421L1.70711 15.7071C1.31658 16.0976 0.683417 16.0976 0.292893 15.7071C-0.0976311 15.3166 -0.0976311 14.6834 0.292893 14.2929L6.58579 8L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
                      fill="#6E6E6E"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2539_8277">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>

            <div class="datepicker-calendar">
              <div class="datepicker-days sticky">
                <span>{{ $t('location_form.move_date.weekday_short.monday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.tuesday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.wednesday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.thursday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.friday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.saturday') }}</span>
                <span>{{ $t('location_form.move_date.weekday_short.sunday') }}</span>
              </div>
              <div class="modal-body">
                <div v-for="month in months" :key="month.key">
                  <div class="datepicker-month-label">{{ month.label }}</div>
                  <div class="datepicker-grid">
                    <span
                      v-for="day in month.days"
                      :key="day.key"
                      :class="getDayClasses(day)"
                      @click="day.isClickable ? handleDateSelected(day.date) : null"
                    >
                      {{ day.display }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue"
import { CATEGORY_V4_ID_HOUSE_MOVE } from "~/constants/category"
import { getV4UrlByCategoryId } from "~/services/public/Listing"
import { useNuxtApp } from "#app"

const props = defineProps<{
  initialDate?: string | null
  doesNotHaveMoveDate?: boolean
  text: {
    noMoveDate: string
    prompt: string
    saveButton: string
    chooseDate: string
  }
  locale: string
}>()

const emit = defineEmits<{
  moveDate: [value: string | null]
}>()

const { $snowplow, $config } = useNuxtApp()

const isModalOpen = ref(false)
const moveDate = ref<string | null>(props.initialDate ?? null)

const openModal = () => {
  if (!props.doesNotHaveMoveDate) {
    isModalOpen.value = true
  }
  $snowplow.trackButtonClick(
    "Opened Move Date Selector Modal",
    $config.public.v4Host + "/" + getV4UrlByCategoryId(CATEGORY_V4_ID_HOUSE_MOVE)
  )
}

const closeModal = () => {
  isModalOpen.value = false
}

const handleOverlayClick = (event: Event) => {
  if (event.target === event.currentTarget) {
    closeModal()
  }
}

const today = new Date()
const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))

const displayText = computed(() => {
  if (props.doesNotHaveMoveDate) {
    return props.text.noMoveDate
  }
  if (moveDate.value === null) {
    return props.text.prompt
  }
  const date = new Date(moveDate.value)
  return date.toLocaleDateString(props.locale, {
    day: "numeric",
    month: "long",
    year: "numeric"
  })
})

watch(
  () => props.doesNotHaveMoveDate,
  newDoesNotHaveMoveDate => {
    if (newDoesNotHaveMoveDate) {
      moveDate.value = null
    }
  }
)

interface Day {
  key: string
  display: string
  date: Date | null
  isClickable: boolean
  isToday: boolean
  isPast: boolean
  isSelected: boolean
}

interface Month {
  key: string
  label: string
  days: Day[]
}

const MONTHS_TO_DISPLAY = 9

const months = computed<Month[]>(() => {
  const result: Month[] = []

  const isFirstOfMonth = todayUTC.getUTCDate() === 1

  let currentDate: Date

  if (isFirstOfMonth) {
    currentDate = new Date(Date.UTC(todayUTC.getUTCFullYear(), todayUTC.getUTCMonth(), 1))
  } else {
    const todayDayOfWeek = todayUTC.getUTCDay()
    const mondayOffset = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1
    const startOfWeek = new Date(todayUTC)
    startOfWeek.setUTCDate(todayUTC.getUTCDate() - mondayOffset)

    currentDate = new Date(Date.UTC(startOfWeek.getUTCFullYear(), startOfWeek.getUTCMonth(), 1))
  }

  for (let i = 0; i < MONTHS_TO_DISPLAY; i++) {
    const monthDate = new Date(
      Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth() + i, 1)
    )
    const monthLabel = monthDate.toLocaleDateString(props.locale, {
      month: "long",
      year: "numeric",
      timeZone: "UTC"
    })

    const days: Day[] = []

    const firstDayOfWeek = monthDate.getUTCDay()
    const mondayFirstDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1

    if (i === 0) {
      let startDay: number

      if (isFirstOfMonth) {
        startDay = 1
      } else {
        const todayDayOfWeek = todayUTC.getUTCDay()
        const mondayOffset = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1
        const startOfWeek = new Date(todayUTC)
        startOfWeek.setUTCDate(todayUTC.getUTCDate() - mondayOffset)

        const startOfWeekInMonth =
          startOfWeek.getUTCMonth() === monthDate.getUTCMonth() ? startOfWeek.getUTCDate() : 1
        startDay = Math.max(1, startOfWeekInMonth)
      }

      const startDate = new Date(
        Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth(), startDay)
      )
      const startDayOfWeek = startDate.getUTCDay()
      const startMondayFirstDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1

      for (let j = 0; j < startMondayFirstDay; j++) {
        days.push({
          key: `${monthDate.getUTCFullYear()}-${monthDate.getUTCMonth()}-empty-${j}`,
          display: " ",
          date: null,
          isClickable: false,
          isToday: false,
          isPast: false,
          isSelected: false
        })
      }

      const lastDay = new Date(
        Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + 1, 0)
      ).getUTCDate()

      for (let day = startDay; day <= lastDay; day++) {
        const dayDate = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth(), day))
        const isToday = dayDate.toISOString().split("T")[0] === todayUTC.toISOString().split("T")[0]
        const isPast = dayDate < todayUTC && !isToday
        const isSelected = moveDate.value === dayDate.toISOString().split("T")[0]

        days.push({
          key: `${monthDate.getUTCFullYear()}-${monthDate.getUTCMonth()}-${day}`,
          display: day.toString(),
          date: dayDate,
          isClickable: !isPast,
          isToday,
          isPast,
          isSelected
        })
      }
    } else {
      for (let j = 0; j < mondayFirstDay; j++) {
        days.push({
          key: `${monthDate.getUTCFullYear()}-${monthDate.getUTCMonth()}-empty-${j}`,
          display: " ",
          date: null,
          isClickable: false,
          isToday: false,
          isPast: false,
          isSelected: false
        })
      }

      const lastDay = new Date(
        Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth() + 1, 0)
      ).getUTCDate()

      for (let day = 1; day <= lastDay; day++) {
        const dayDate = new Date(Date.UTC(monthDate.getUTCFullYear(), monthDate.getUTCMonth(), day))
        const isToday = dayDate.toISOString().split("T")[0] === todayUTC.toISOString().split("T")[0]
        const isPast = dayDate < todayUTC && !isToday
        const isSelected = moveDate.value === dayDate.toISOString().split("T")[0]

        days.push({
          key: `${monthDate.getUTCFullYear()}-${monthDate.getUTCMonth()}-${day}`,
          display: day.toString(),
          date: dayDate,
          isClickable: !isPast,
          isToday,
          isPast,
          isSelected
        })
      }
    }

    result.push({
      key: `${monthDate.getUTCFullYear()}-${monthDate.getUTCMonth()}`,
      label: monthLabel,
      days
    })
  }

  return result
})

function handleDateSelected(date: Date | null) {
  if (date) {
    moveDate.value = date.toISOString().split("T")[0]
    emit("moveDate", moveDate.value)
    closeModal()
  }
}

function getDayClasses(day: Day): string[] {
  const classes = ["datepicker-day"]

  if (day.isPast) {
    classes.push("disabled")
  }

  if (day.isToday) {
    classes.push("current-day")
  }

  if (day.isSelected) {
    classes.push("selected")
  }

  return classes
}
</script>

<style>
.modal-overlay {
  @apply fixed inset-0 z-50 overflow-auto bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center;

  .modal-content {
    @apply relative w-full max-w-md px-ds-ml py-ds-md bg-white rounded-2xl mx-ds-md mb-[40px];
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  .modal-enter-active {
    transition:
      opacity 0.3s ease-out,
      backdrop-filter 0.3s ease-out;
  }

  .modal-leave-active {
    transition:
      opacity 0.5s ease-out,
      backdrop-filter 0.5s ease-out;
  }

  .modal-enter-active .modal-content {
    transition:
      transform 0.3s ease-out,
      opacity 0.3s ease-out;
  }

  .modal-leave-active .modal-content {
    transition:
      transform 0.5s ease-out,
      opacity 0.5s ease-out;
  }

  .modal-enter-from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }

  .modal-enter-from .modal-content {
    opacity: 0;
    transform: translate3d(0, -20px, 0);
  }

  .modal-leave-to {
    opacity: 0;
    backdrop-filter: blur(0px);
  }

  .modal-leave-to .modal-content {
    opacity: 0;
    transform: translate3d(0, -20px, 0);
  }

  .modal-header {
    @apply flex justify-between items-center pb-ds-md mb-ds-md border-b border-[#c9c9c9] leading-xlb;

    .modal-title {
      @apply text-xl font-semibold text-[hsl(0_0%_0%/0.75)];
    }

    .modal-close {
      @apply text-gray-500 cursor-pointer rounded-lg;

      .modal-close-icon {
        @apply w-ds-ml h-ds-ml;
      }
    }
  }

  .modal-body {
    @apply h-[384px] overflow-y-scroll;

    .modal-text {
      @apply text-gray-700 mb-ds-md last:mb-0;
    }
  }

  .modal-body::-webkit-scrollbar {
    @apply hidden;
  }

  .modal-footer {
    @apply flex flex-col gap-ds-sm;

    .modal-btn {
      @apply px-ds-md py-ds-sm rounded-lg transition-colors bg-[#eb5661] w-full cursor-pointer text-white;
    }
  }

  .datepicker-days {
    @apply grid grid-cols-7 text-[hsl(0_0%_61%)] text-xs font-normal sticky top-0 bg-white py-ds-sm cursor-pointer;
  }

  .datepicker-days > span {
    @apply justify-self-center;
  }

  .datepicker-month-label {
    @apply text-center font-bold my-ds-md mx-ds-sm;
  }

  .datepicker-grid {
    @apply grid grid-cols-7;
  }

  .datepicker-grid > span {
    @apply justify-self-center;
  }

  .datepicker-day {
    @apply flex justify-center items-center h-ds-lg w-ds-lg rounded-full text-base cursor-pointer font-bold m-ds-xs transition-[background] text-[hsl(0_0%_0%_/_0.75)];
  }

  .datepicker-day.current-day {
    @apply bg-[#41a5dd] text-white;
  }

  .datepicker-day.selected {
    @apply bg-[#41a5dd] text-white rounded-lg m-0 h-ds-xl w-ds-xl font-normal;
  }

  .datepicker-day.disabled {
    @apply text-[#c9c9c9] pointer-events-none font-normal;
  }

  .datepicker-footer {
    @apply p-ds-ml flex flex-col absolute bottom-0 bg-white w-full left-0 right-0 mx-auto z-10 lg:rounded-bl-lg lg:rounded-br-lg;
    box-shadow: 0 -22px 20px rgba(0, 0, 0, 0.2);
  }
}

.av-input-button {
  @apply relative flex py-ds-md pl-ds-md pr-ds-lg w-full h-auto resize-none text-gray-600 border border-mono-200 rounded-md bg-white cursor-pointer;

  span:first-of-type {
    @apply text-lg whitespace-nowrap overflow-hidden text-ellipsis;
  }

  span + [class*="icon-"] {
    @apply absolute text-[rgba(41,41,41)] right-ds-md top-1/2 -translate-y-1/2;
    & > svg {
      @apply h-ds-md w-ds-md;
    }
  }

  &.input-selected {
    @apply text-[#292929];
  }

  &.disabled {
    @apply cursor-default;
  }

  .selected-date.disabled-text {
    @apply text-gray-600;
  }
}
</style>
